import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import {
  TTransactionListItem,
  TransactionsList,
} from '@/common/components/TransactionsList/TransactionsList';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { DateUtils, INTL_MONTH_YEAR_FORMAT } from '@/common/utils/date';
import { useInsuranceMovements } from '@/web/hooks/useInsuranceMovements';
import { ArrayUtils } from '@coverflex-tech/hypernova-utils';

export type TInsuranceActivityProps = {
  policyId: string;
};

export function InsuranceActivity({ policyId }: TInsuranceActivityProps) {
  const { t } = useTranslation();
  const { mergedMovements, payrollMovements, walletMovements } =
    useInsuranceMovements(policyId);
  const [activeFilter, setActiveFilter] = useState<
    'all' | 'wallet' | 'payroll'
  >('all');
  const { employee } = useEmployeeDetails();

  const groupedMovements = useMemo(() => {
    if (!mergedMovements) {
      return {};
    }
    const filteredMovements = [
      ...mergedMovements.filter((movement) =>
        activeFilter === 'all' ? true : movement.type === activeFilter
      ),
    ];
    return ArrayUtils.groupBy(
      ArrayUtils.orderBy(
        filteredMovements,
        (movement) => movement.date,
        'desc'
      ),
      (movement) => {
        return DateUtils.intlFormat(movement.date, INTL_MONTH_YEAR_FORMAT, {
          language: employee.language,
        });
      }
    );
  }, [mergedMovements, activeFilter, employee]);

  return (
    <Stack gap="md">
      {walletMovements?.length && payrollMovements?.length ? (
        <Stack horizontal gap="md">
          <Button
            variant="tertiary"
            onPress={() => setActiveFilter('all')}
            icon={<Icon icon="right-left" />}
            size="sm"
            disabled={activeFilter === 'all'}
          >
            {t('dashboard.insurance_detail.activity.filters.all')}
          </Button>
          <Button
            variant="tertiary"
            onPress={() => setActiveFilter('wallet')}
            icon={<Icon icon="seedling" />}
            size="sm"
            disabled={activeFilter === 'wallet'}
          >
            {t('dashboard.insurance_detail.activity.filters.wallet')}
          </Button>

          <Button
            variant="tertiary"
            onPress={() => setActiveFilter('payroll')}
            icon={<Icon icon="receipt" />}
            size="sm"
            disabled={activeFilter === 'payroll'}
          >
            {t('dashboard.insurance_detail.activity.filters.payroll')}
          </Button>
        </Stack>
      ) : null}
      <Stack gap="page">
        {Object.entries(groupedMovements).map(([key, movements]) => {
          const rows: TTransactionListItem[] = movements.map((movement) => {
            return {
              icon:
                movement.type === 'wallet' ? (
                  <Icon icon="seedling" color="accentSecondary10" />
                ) : (
                  <Icon icon="receipt" color="accentSecondary10" />
                ),
              date: movement.date,
              description: t('transaction_list.item.description.health', {
                short_name: movement.memberShortName,
              }),
              amount: movement.amount,
              isDebit: true,
            };
          });

          return (
            <Stack gap="md" key={key}>
              <BodyText bold>{key}</BodyText>
              <TransactionsList transactions={rows} />
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
